/**
 * /es/testimonios — Rich testimonials page mirroring /testimonials.
 * Adds: hero, GoogleReviewsWidget (live data), 3 trust-badge cards,
 * patient quotes, "leave a review" CTA, and final CTA banner.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import EsSEO from '@/components/seo/EsSEO';
import GoogleReviewsWidget from '@/components/common/GoogleReviewsWidget';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';

const SITE = 'https://fortworthdental.com';

const TRUST_BADGES = [
  {
    icon: '🏆',
    title: 'Atención Galardonada',
    desc: 'Reconocidos como Best of Fort Worth 2025 — más de 1,100 reseñas de 5 estrellas en Google.',
  },
  {
    icon: '💙',
    title: 'Enfocados en el Paciente',
    desc: 'Atención compasiva, explicaciones en español y planes de tratamiento personalizados para tu familia.',
  },
  {
    icon: '🦷',
    title: 'Equipo Experto',
    desc: 'Dr. Mund (Diplomado en Implantología), Dra. Razo, Dr. Hiwiadi y Dr. Pham — más de 80 años combinados de experiencia.',
  },
];

const PATIENT_QUOTES = [
  {
    body:
      'El Dr. Mund cambió mi vida. Tenía miedo de los implantes pero todo el equipo me hizo sentir cómoda — explicaron todo en español y los resultados son perfectos.',
    author: 'María G., Fort Worth',
  },
  {
    body:
      'Mi hija necesitaba frenos y nos atendieron sin cita. El personal habla español, los precios son honestos y los pagos mensuales hacen todo accesible.',
    author: 'Carlos R., Arlington',
  },
  {
    body:
      'Llevamos 8 años trayendo a toda la familia. La limpieza y los exámenes siempre son completos. Los recomiendo 100%.',
    author: 'Familia Hernández, Benbrook',
  },
];

const SpanishTestimonialsPage = () => {
  const { displayNumber, telLink } = useSpanishTrackingNumber();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Testimonios — Fort Worth Dental',
    inLanguage: 'es-US',
    url: `${SITE}/es/testimonios`,
    about: { '@type': 'Dentist', name: 'Fort Worth Dental' },
  };

  return (
    <div className="min-h-screen bg-white">
      <EsSEO
        title="Testimonios de Pacientes — Fort Worth Dental"
        description="Lee testimonios de pacientes de Fort Worth Dental. Más de 1,100 reseñas de 5 estrellas en Google de familias en Fort Worth, Aledo, Benbrook y áreas cercanas."
        canonical="/es/testimonios"
        hreflangEn="/testimonials"
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 text-white py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Testimonios</h1>
          <p className="text-lg text-teal-50 max-w-3xl">
            Más de 1,100 reseñas de 5 estrellas en Google — la confianza de las familias de Tarrant County desde 1990.
          </p>
        </div>
      </section>

      {/* Google Reviews live widget */}
      <section className="py-16 bg-white" data-testid="es-testimonials-google">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-teal-600 font-semibold text-lg mb-2">Reseñas en Vivo de Google</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800">Lo Que Dicen Nuestros Pacientes</h2>
          </div>
          <div className="max-w-xl mx-auto">
            <GoogleReviewsWidget variant="compact" />
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="py-16 bg-gray-50" data-testid="es-testimonials-badges">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-lg mb-2">¿Tuviste una Buena Experiencia?</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800">Por Qué los Pacientes Nos Eligen</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TRUST_BADGES.map((b) => (
              <div key={b.title} className="bg-white p-8 rounded-lg shadow text-center">
                <div className="text-5xl mb-4" aria-hidden="true">{b.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{b.title}</h3>
                <p className="text-gray-600">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Patient quotes */}
      <section className="py-16 bg-white" data-testid="es-testimonials-quotes">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">Historias de Familias Hispanas</h2>
          <div className="space-y-6">
            {PATIENT_QUOTES.map((q, i) => (
              <blockquote key={i} className="bg-gray-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
                <p className="text-gray-700 italic leading-relaxed mb-3">"{q.body}"</p>
                <footer className="text-sm font-semibold text-gray-900">— {q.author}</footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Leave a review */}
      <section className="py-16 bg-amber-50" data-testid="es-testimonials-leave-review">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">¿Quieres Dejar tu Reseña?</h2>
          <p className="text-gray-700 text-lg mb-6">
            Si tuviste una buena experiencia, nos encantaría que la compartieras en Google — ayudas a otras familias a encontrarnos.
          </p>
          <a
            href="https://search.google.com/local/writereview?placeid=ChIJv0ra4mFtToYRSiUHQ8Fyu7o"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-bold px-8 py-4 rounded-lg text-lg"
          >
            Escribe tu Reseña en Google
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-teal-600 text-white" data-testid="es-testimonials-cta">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">¿Listo para la Mejor Atención Dental de Fort Worth?</h2>
          <p className="text-xl mb-8">Únete a los 1,100+ pacientes que nos califican con 5 estrellas — agenda tu cita hoy.</p>
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-6">
            <a href={telLink} className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100">
              Llamar a {displayNumber}
            </a>
            <Link
              to="/es/contacto"
              className="bg-yellow-500 text-yellow-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-400"
            >
              Reservar Cita
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SpanishTestimonialsPage;
